import { Box, Button, Modal, TextField, Typography } from "@mui/material";

import { Controller, useForm, useWatch } from "react-hook-form";

import { useEffect } from "react";
import { PutPlanFields } from "../../../interfaces/SuperAdmin/Pages/iDataPlan";
import { useSuperAdminPlans } from "../../../context/SuperAdmin/PlanContext";

const EditPlanModal = () => {
  const {
    toggleEditModal,
    isEditing,
    isEditModalOpen,
    selectedPlan,
    planEdit,
  } = useSuperAdminPlans();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm<PutPlanFields>();

  const duracion = useWatch({ control, name: "duracion" }) || 0;
  const creditosMes = useWatch({ control, name: "creditos_mensuales" }) || 0;
  const maxUsuarios = useWatch({ control, name: "max_usuarios" }) || 0;

  useEffect(() => {
    setValue("total_creditos", duracion * creditosMes * maxUsuarios);
  }, [duracion, creditosMes, maxUsuarios, setValue]);

  // Función para manejar el envío del formulario
  const handleSubmitForm = (data: PutPlanFields) => {
    if (selectedPlan) {
      console.log(data);
      planEdit(selectedPlan.id_plan, data);
    }
  };

  useEffect(() => {
    if (selectedPlan) {
      const {
        nombre,
        descripcion,
        precio,
        duracion,
        creditos_mensuales,
        max_usuarios,
        total_creditos,
      } = selectedPlan;
      reset({
        nombre,
        descripcion,
        precio,
        duracion,
        creditos_mensuales,
        max_usuarios,
        total_creditos,
      });
    }
  }, [selectedPlan]);

  const handleClose = () => {
    if (!isEditing) {
      reset();
      toggleEditModal(false);
    }
  };

  return (
    <Modal
      open={isEditModalOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)",
      }}
    >
      <Box
        component="form"
        sx={{
          width: 900,
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <Typography variant="h6" component="h2">
          Editar plan
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Nombre
            </Typography>
            <Controller
              name="nombre"
              control={control}
              rules={{ required: "El nombre es obligatorio" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="normal"
                  error={!!errors.nombre}
                  helperText={errors.nombre?.message}
                  disabled={isEditing}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Descripción
            </Typography>
            <Controller
              name="descripcion"
              control={control}
              rules={{ required: "La descripción es obligatoria" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="normal"
                  error={!!errors.descripcion}
                  helperText={errors.descripcion?.message}
                  disabled={isEditing}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Precio (USD)
            </Typography>
            <Controller
              name="precio"
              control={control}
              rules={{
                required: "El precio es obligatorio",
                min: { value: 1, message: "No puede ser 0" },
                validate: (value) => {
                  if (!Number.isInteger(Number(value))) {
                    return "Debe ser un número entero";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!errors.precio}
                  helperText={errors.precio?.message}
                  disabled={isEditing}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Duración (meses)
            </Typography>
            <Controller
              name="duracion"
              control={control}
              rules={{
                required: "La duración es obligatoria",
                min: { value: 1, message: "No puede ser 0" },
                validate: (value) => {
                  if (!Number.isInteger(Number(value))) {
                    return "Debe ser un número entero";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!errors.duracion}
                  helperText={errors.duracion?.message}
                  disabled={isEditing}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Créditos / mes
            </Typography>
            <Controller
              name="creditos_mensuales"
              control={control}
              rules={{
                required: "Los créditos por mes son obligatorios",
                min: { value: 1, message: "No puede ser 0" },
                validate: (value) => {
                  if (!Number.isInteger(Number(value))) {
                    return "Debe ser un número entero";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!errors.creditos_mensuales}
                  helperText={errors.creditos_mensuales?.message}
                  disabled={isEditing}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Máximo de Usuarios
            </Typography>
            <Controller
              name="max_usuarios"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!errors.max_usuarios}
                  helperText={errors.max_usuarios?.message}
                  disabled={isEditing}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Total de créditos
            </Typography>
            <TextField
              value={duracion * creditosMes * maxUsuarios}
              fullWidth
              margin="normal"
              disabled
            />
          </Box>
        </Box>

        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button
            variant="contained"
            sx={{ background: "#336B87" }}
            disabled={isEditing}
            type="submit"
          >
            {isEditing ? "Guardando" : "Guardar"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditPlanModal;
